'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  ArrowRight,
  Check,
  ChevronDown,
  LayoutList,
  Loader2,
  Map as MapIcon,
  MapPin,
} from 'lucide-react';
import { useFormatter, useLocale, useTranslations } from 'next-intl';
import { trpc } from '@/lib/trpc';
import { useRegional } from '@/components/providers/regional-provider';
import { formatPublicCurrency } from '@/lib/format/currency-display';
import { buildLocalePath, type Locale } from '@/i18n/config';
import { cn } from '@/lib/utils';
import type { GeoJSONData, MapboxMap, MapboxMapEvent } from '@/types/mapbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu/dropdown-menu';

const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN || '';

type CountryTab = 'AU' | 'NZ';
type ViewMode = 'list' | 'map';

const COUNTRY_TABS: Array<{
  code: CountryTab;
  flag: string;
  labelKey: 'australia' | 'newZealand';
  slug: string;
}> = [
  { code: 'AU', flag: '🇦🇺', labelKey: 'australia', slug: 'australia' },
  { code: 'NZ', flag: '🇳🇿', labelKey: 'newZealand', slug: 'new-zealand' },
];

const COUNTRY_VIEWS: Record<CountryTab, { center: [number, number]; zoom: number }> = {
  AU: { center: [134, -27], zoom: 3.4 },
  NZ: { center: [172, -41], zoom: 4.6 },
};

interface GlobalRideRow {
  id: string;
  city: string;
  slug: string;
  stateName: string | null;
  stateCode: string | null;
  countryName: string;
  countryCode: string;
  eventDate: string | null;
  startTime: string | null;
  capacity: string | number | null;
  currentRegistrations: number;
  raised: number;
  donationCount: number;
  currencyCode: string;
  coordinates: string | null;
}

function parseCoords(value: string | null): { lat: number; lng: number } | null {
  if (!value) return null;
  const [lat, lng] = value.split(',').map((s) => Number(s.trim()));
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return { lat, lng };
}

function formatEventDate(iso: string | null, locale: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

interface EventsPageProps {
  prefetchedData?: { countryEventStats?: unknown; currencyCode?: string };
  initialView?: ViewMode;
}

export function EventsPage({ initialView = 'list' }: EventsPageProps) {
  const tEvents = useTranslations('events');
  const tRideWith = useTranslations('uwrRideWith');
  const tUwrEvents = useTranslations('uwrEvents');
  const format = useFormatter();
  const locale = useLocale() as Locale;
  const { currency: regionalCurrency } = useRegional();

  const [activeCountry, setActiveCountry] = useState<CountryTab>('AU');
  const [viewMode, setViewMode] = useState<ViewMode>(initialView);
  // Direction of the country switch — used to pick which slide animation
  // (right-to-left vs left-to-right) plays on the content swap.
  const prevCountryIndexRef = useRef(0);
  const [slideDirection, setSlideDirection] = useState<'right' | 'left'>('right');
  const handleCountryChange = useCallback((next: CountryTab) => {
    const nextIndex = COUNTRY_TABS.findIndex((t) => t.code === next);
    setSlideDirection(nextIndex >= prevCountryIndexRef.current ? 'right' : 'left');
    prevCountryIndexRef.current = nextIndex;
    setActiveCountry(next);
  }, []);

  const { data: globalRidesRaw = [], isLoading } = trpc.publicLookups.globalMapRides.useQuery({
    currencyCode: regionalCurrency === 'NZD' ? 'NZD' : 'AUD',
  });
  const globalRides = globalRidesRaw as GlobalRideRow[];

  const ridesForCountry = useMemo(
    () => globalRides.filter((r) => r.countryCode === activeCountry),
    [globalRides, activeCountry],
  );

  const totals = useMemo(
    () => ({
      rides: ridesForCountry.length,
      riders: ridesForCountry.reduce((sum, r) => sum + Number(r.currentRegistrations || 0), 0),
      raised: ridesForCountry.reduce((sum, r) => sum + Number(r.raised || 0), 0),
    }),
    [ridesForCountry],
  );

  const activeTab = COUNTRY_TABS.find((t) => t.code === activeCountry) ?? COUNTRY_TABS[0];

  // ───────── Mapbox ─────────
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<MapboxMap | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);

  // Load Mapbox JS + CSS once (only when entering map mode)
  useEffect(() => {
    if (viewMode !== 'map' || !MAPBOX_TOKEN) return;

    // One-time custom popup CSS so the close button renders as a styled circle
    // instead of the bare default `×`.
    if (!document.querySelector('style[data-mapbox-uwr-custom]')) {
      const style = document.createElement('style');
      style.setAttribute('data-mapbox-uwr-custom', 'true');
      style.textContent = `
        .mapboxgl-popup { z-index: 10; }
        .mapboxgl-popup-content {
          padding: 0 !important;
          border-radius: 12px !important;
          box-shadow: 0 10px 40px rgba(0,0,0,0.18) !important;
          overflow: visible;
        }
        .mapboxgl-popup-close-button {
          position: absolute;
          top: -12px; right: -12px;
          display: flex; align-items: center; justify-content: center;
          width: 26px; height: 26px;
          font-size: 16px; line-height: 1; font-weight: 600;
          color: #ffffff;
          background: #1c1917 !important;
          border: 2px solid #ffffff;
          border-radius: 9999px;
          padding: 0 !important;
          cursor: pointer;
          transition: transform 0.15s ease;
        }
        .mapboxgl-popup-close-button:hover { transform: scale(1.08); }
        .mapboxgl-popup-tip { border-top-color: #ffffff !important; }
        .mapboxgl-ctrl-logo, .mapboxgl-ctrl-attrib { display: none !important; }
      `;
      document.head.appendChild(style);
    }

    if (window.mapboxgl) {
      queueMicrotask(() => setMapLoaded(true));
      return;
    }

    if (!document.querySelector('link[href*="mapbox-gl"]')) {
      const link = document.createElement('link');
      link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }

    const existingScript = document.querySelector('script[src*="mapbox-gl"]');
    if (existingScript) {
      if (window.mapboxgl) {
        queueMicrotask(() => setMapLoaded(true));
      } else {
        existingScript.addEventListener('load', () => setMapLoaded(true));
      }
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js';
    script.async = true;
    script.onload = () => setMapLoaded(true);
    document.head.appendChild(script);
  }, [viewMode]);

  // Initialise the map when entering map mode
  useEffect(() => {
    if (viewMode !== 'map') return;
    if (!mapLoaded || !mapContainer.current || !MAPBOX_TOKEN || mapInstance.current) return;

    const mapboxgl = window.mapboxgl;
    if (!mapboxgl) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;

    const view = COUNTRY_VIEWS[activeCountry];
    mapInstance.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: view.center,
      zoom: view.zoom,
      minZoom: 2,
      maxZoom: 14,
    });

    mapInstance.current.on('load', () => setMapInitialized(true));

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
      setMapInitialized(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded, viewMode]);

  // Render pins for active country
  const renderPins = useCallback(() => {
    if (viewMode !== 'map') return;
    if (!mapInitialized || !mapInstance.current || !window.mapboxgl) return;
    const m = mapInstance.current;
    const mapboxgl = window.mapboxgl;

    if (m.getLayer('uwr-pins')) m.removeLayer('uwr-pins');
    if (m.getSource('uwr-rides')) m.removeSource('uwr-rides');

    const features = ridesForCountry
      .map((ride) => {
        const coords = parseCoords(ride.coordinates);
        if (!coords) return null;
        return {
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [coords.lng, coords.lat] },
          properties: {
            id: ride.id,
            city: ride.city,
            slug: ride.slug,
            countryName: ride.countryName,
            countryCode: ride.countryCode,
            stateName: ride.stateName ?? '',
            riders: ride.currentRegistrations,
            raised: ride.raised,
          },
        };
      })
      .filter(Boolean);

    m.addSource('uwr-rides', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features } as unknown as GeoJSONData,
    });

    m.addLayer({
      id: 'uwr-pins',
      type: 'circle',
      source: 'uwr-rides',
      paint: {
        'circle-color': 'hsl(19, 88%, 56%)',
        'circle-radius': 8,
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff',
      } as Record<string, unknown>,
    });

    m.on('click', 'uwr-pins', (e: MapboxMapEvent) => {
      const feat = m.queryRenderedFeatures(e.point, { layers: ['uwr-pins'] });
      if (!feat.length) return;
      const props = feat[0].properties as Record<string, unknown>;
      const coords = feat[0].geometry.coordinates as unknown as [number, number];
      const countrySlug = activeCountry === 'AU' ? 'australia' : 'new-zealand';
      const formattedRaised = format.number(props.raised as number, {
        style: 'currency',
        currency: regionalCurrency,
        notation: 'compact',
        maximumFractionDigits: 0,
      });
      new mapboxgl.Popup({ offset: 14, closeButton: true })
        .setLngLat(coords)
        .setHTML(
          `<div style="padding:14px;min-width:220px;font-family:system-ui,sans-serif;">
            <h3 style="font-weight:600;margin:0 0 4px;font-size:16px;color:#1c1917;">${props.city}</h3>
            ${props.stateName ? `<p style="margin:0 0 10px;font-size:12px;color:#78716c;">${props.stateName}</p>` : ''}
            <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:6px;margin-bottom:12px;font-size:11px;">
              <div>
                <p style="margin:0;color:#78716c;">${tEvents('riders')}</p>
                <p style="margin:0;font-weight:600;color:#1c1917;">${format.number(props.riders as number)}</p>
              </div>
              <div>
                <p style="margin:0;color:#78716c;">${tEvents('raised')}</p>
                <p style="margin:0;font-weight:600;color:hsl(19 88% 56%);">${formattedRaised}</p>
              </div>
            </div>
            <a href="/events/${countrySlug}/${props.slug}"
               style="display:block;text-align:center;padding:8px 14px;background:#1c1917;color:white;border-radius:8px;font-size:12px;font-weight:500;text-decoration:none;">
              ${tEvents('viewEvent')}
            </a>
          </div>`,
        )
        .addTo(m);
    });

    m.on('mouseenter', 'uwr-pins', () => {
      m.getCanvas().style.cursor = 'pointer';
    });
    m.on('mouseleave', 'uwr-pins', () => {
      m.getCanvas().style.cursor = '';
    });
  }, [viewMode, mapInitialized, ridesForCountry, activeCountry, format, regionalCurrency, tEvents]);

  // Guard against the dev-mode StrictMode double-mount race where the map's
  // 'load' fires before its style is fully ready — addSource/addLayer silently
  // no-op in that window.
  useEffect(() => {
    const m = mapInstance.current;
    if (!m) return;
    if (m.isStyleLoaded()) {
      renderPins();
    } else {
      m.once('idle', renderPins);
    }
  }, [renderPins]);

  // Fly to country on tab change
  useEffect(() => {
    if (viewMode !== 'map') return;
    if (!mapInitialized || !mapInstance.current) return;
    const view = COUNTRY_VIEWS[activeCountry];
    mapInstance.current.flyTo({ center: view.center, zoom: view.zoom, duration: 1400 });
  }, [activeCountry, mapInitialized, viewMode]);

  return (
    <main className="flex-1">
      {/* Hero */}
      <section className="relative h-[140px] md:h-[360px]">
        <Image
          src="/images/uwr/HeroImageUWR.jpg"
          alt={tEvents('eventsWorldwide')}
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-stone-950/40 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center">
          <h1 className="text-2xl md:text-5xl font-bold text-white">{tRideWith('title')}</h1>
        </div>
      </section>

      {/* Control bar — country tabs centered, view toggle right. */}
      <section className="border-b border-stone-200 bg-white">
        <div className="container py-3 md:py-4 grid grid-cols-[1fr_auto_1fr] items-center gap-2 md:gap-3">
          {/* Spacer (keeps tabs centered against the right-aligned view toggle) */}
          <div className="hidden md:block" />

          {/* Country tabs */}
          <div className="inline-flex items-center gap-1 md:gap-2 rounded-full bg-stone-100 p-1 mx-auto col-start-2">
            {COUNTRY_TABS.map((tab) => {
              const active = activeCountry === tab.code;
              return (
                <button
                  key={tab.code}
                  type="button"
                  onClick={() => handleCountryChange(tab.code)}
                  className={cn(
                    'flex items-center gap-1.5 md:gap-2 px-3 md:px-5 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-semibold transition-colors',
                    active ? 'bg-stone-900 text-white' : 'text-stone-600 hover:text-stone-900',
                  )}
                >
                  <span aria-hidden>{tab.flag}</span>
                  {/* Acronym on mobile (AU/NZ), full name on desktop. */}
                  <span className="md:hidden">{tab.code}</span>
                  <span className="hidden md:inline">{tRideWith(tab.labelKey)}</span>
                </button>
              );
            })}
          </div>

          {/* View toggle — pills on desktop, compact dropdown on mobile */}
          <div className="flex justify-end col-start-3">
            {/* Desktop: pill toggle */}
            <div className="hidden md:inline-flex items-center rounded-full border border-stone-200 p-1">
              {(['list', 'map'] as const).map((mode) => {
                const active = viewMode === mode;
                const Icon = mode === 'list' ? LayoutList : MapIcon;
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setViewMode(mode)}
                    aria-pressed={active}
                    className={cn(
                      'flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors',
                      active ? 'bg-uwr-orange text-white' : 'text-stone-600 hover:text-stone-900',
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tUwrEvents(mode === 'list' ? 'listView' : 'mapView')}</span>
                  </button>
                );
              })}
            </div>

            {/* Mobile: dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="md:hidden inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-sm font-semibold text-stone-700"
                  aria-label={tUwrEvents(viewMode === 'list' ? 'listView' : 'mapView')}
                >
                  {viewMode === 'list' ? (
                    <LayoutList className="w-4 h-4" />
                  ) : (
                    <MapIcon className="w-4 h-4" />
                  )}
                  <ChevronDown className="w-3.5 h-3.5 text-stone-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-36">
                {(['list', 'map'] as const).map((mode) => {
                  const Icon = mode === 'list' ? LayoutList : MapIcon;
                  return (
                    <DropdownMenuItem
                      key={mode}
                      onClick={() => setViewMode(mode)}
                      className="cursor-pointer gap-2"
                    >
                      <Icon className="w-4 h-4 text-stone-500" />
                      <span className="flex-1">
                        {tUwrEvents(mode === 'list' ? 'listView' : 'mapView')}
                      </span>
                      {viewMode === mode && <Check className="w-4 h-4 text-uwr-orange" />}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-b border-stone-200 bg-stone-50">
        <div className="container max-w-6xl py-6">
          <div className="grid grid-cols-3 gap-4 md:gap-6">
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-uwr-orange-hover">
                {formatPublicCurrency(format, totals.raised, regionalCurrency)}
              </p>
              <p className="text-stone-500 text-sm">{tEvents('raised')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-stone-800">{totals.rides}</p>
              <p className="text-stone-500 text-sm">{tEvents('rides')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-stone-800">
                {format.number(totals.riders)}
              </p>
              <p className="text-stone-500 text-sm">{tEvents('riders')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-10 sm:py-14 bg-stone-50 overflow-x-hidden">
        <div className="container max-w-6xl">
          {viewMode === 'list' ? (
            // Keyed wrapper triggers the directional slide-in animation on every
            // country switch — `key={activeCountry}` forces a fresh mount.
            <div
              key={activeCountry}
              className={cn(
                slideDirection === 'right' ? 'animate-slide-in-right' : 'animate-slide-in-left',
              )}
            >
              {isLoading ? (
                <div className="py-16 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-uwr-orange animate-spin" />
                </div>
              ) : ridesForCountry.length === 0 ? (
                <div className="py-16 text-center">
                  <MapPin className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-stone-900 mb-1">
                    {tUwrEvents('noRidesYetTitle')}
                  </h2>
                  <p className="text-stone-500 text-sm">{tUwrEvents('noRidesYetBody')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {ridesForCountry.map((ride) => {
                    const dateLabel = formatEventDate(ride.eventDate, locale);
                    return (
                      <Link
                        key={ride.id}
                        prefetch={false}
                        href={buildLocalePath(locale, `/events/${activeTab.slug}/${ride.slug}`)}
                        className="group bg-white rounded-xl border border-stone-200 p-5 hover:border-uwr-orange hover:shadow-md transition-all"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="text-lg font-bold text-stone-900 group-hover:text-uwr-orange-hover transition-colors">
                              {ride.city}
                            </h3>
                            {ride.stateName && (
                              <p className="text-sm text-stone-500 mt-0.5">{ride.stateName}</p>
                            )}
                          </div>
                          <ArrowRight className="w-5 h-5 shrink-0 text-stone-300 group-hover:text-uwr-orange transition-colors" />
                        </div>
                        {dateLabel && (
                          <p className="mt-3 text-sm font-semibold text-stone-700">{dateLabel}</p>
                        )}
                        <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-stone-500">{tEvents('riders')}</p>
                            <p className="font-semibold text-stone-800">
                              {format.number(ride.currentRegistrations)}
                            </p>
                          </div>
                          <div>
                            <p className="text-stone-500">{tEvents('raised')}</p>
                            <p className="font-semibold text-uwr-orange-hover">
                              {formatPublicCurrency(format, ride.raised, regionalCurrency)}
                            </p>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            // Map view
            <div className="relative mx-auto">
              <div
                ref={mapContainer}
                className="h-[520px] sm:h-[640px] md:h-[720px] w-full rounded-2xl overflow-hidden bg-stone-100"
              />

              {!mapInitialized && MAPBOX_TOKEN && (
                <div className="absolute inset-0 flex items-center justify-center bg-stone-100 rounded-2xl">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-stone-400 mx-auto mb-3" />
                    <p className="text-stone-500 text-sm">{tEvents('loadingGlobalMap')}</p>
                  </div>
                </div>
              )}

              {!MAPBOX_TOKEN && (
                <div className="absolute inset-0 flex items-center justify-center bg-stone-100 rounded-2xl">
                  <div className="text-center">
                    <MapPin className="h-12 w-12 text-stone-300 mx-auto mb-3" />
                    <p className="text-stone-500 text-sm">{tEvents('mapNotAvailable')}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
